import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Maximize, Minimize } from 'lucide-react';
import clsx from 'clsx';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
  fullScreen?: boolean;
  onToggleFullScreen?: () => void;
}

export function Modal({ isOpen, onClose, title, children, size = 'md', footer, fullScreen = false, onToggleFullScreen }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="modal-overlay"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      role="dialog"
      aria-modal
      aria-labelledby="modal-title"
    >
      <div className={clsx('modal', `modal--${size}`, fullScreen && 'modal--fullscreen')}>
        <div className="modal__header">
          <h2 id="modal-title" className="modal__title">{title}</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {onToggleFullScreen && (
              <Button variant="ghost" size="sm" onClick={onToggleFullScreen} aria-label={fullScreen ? 'Contraer' : 'Pantalla completa'}>
                {fullScreen ? <Minimize size={18} /> : <Maximize size={18} />}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </Button>
          </div>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
